// Continuous-scroll PDF viewer (Unit 18, spec §11.2 + Step 7).
//
// Additions vs original:
//   - scrollRef prop: caller can attach a ref to the scroll container for
//     transcript scroll-sync.
//   - onPageHeight callback: fires after each page renders so the parent can
//     build a pageDimensions map for transcript line positioning.
//   - onWordDoubleClick: threaded down to AnnotationOverlay.

import "../lib/pdfWorker";

import { useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import { trackEvent } from "../telemetry/useTelemetry";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import { AnnotationOverlay } from "./AnnotationOverlay";
import type { WordDoubleClickInfo } from "./AnnotationOverlay";
import type { AnnotationType } from "../api/types";

import "./ScanCanvas.css";

export const MIN_ZOOM = 0.4;
export const MAX_ZOOM = 3.0;
const ZOOM_STEP = 0.1;

export interface ScanCanvasProps {
  paperId: number;
  pageCount: number | null;
  initialPage: number;
  onActivePageChange: (page: number) => void;
  /** Controlled zoom level (scale factor). */
  zoom: number;
  /** Called when the user Ctrl+Wheels to request a zoom change. */
  onZoomChange: (zoom: number) => void;
  /** Called once after the first page of each PDF renders, with natural page
   *  dimensions at scale=1 so the parent can compute fit-width / fit-page. */
  onFirstPageRendered?: (naturalW: number, naturalH: number) => void;
  /** Optional ref forwarded to the scroll container (.scan-canvas div). */
  scrollRef?: React.RefObject<HTMLDivElement>;
  /** Called once per page after its canvas renders, with the CSS height. */
  onPageHeight?: (pageNumber: number, heightPx: number) => void;
  /** Called whenever a page's footnote area height changes (0 = no footnotes). */
  onPageFootnoteHeight?: (pageNumber: number, heightPx: number) => void;
  /** Fired when a word with no existing annotation is double-clicked. */
  onWordDoubleClick?: (info: WordDoubleClickInfo) => void;
  /** Scroll handler forwarded to the inner scroll container for sync. */
  onScroll?: () => void;
  /** Which annotation types to show on the canvas. */
  visibleTypes?: Set<AnnotationType>;
  /** When true, pad the scroll area above and below the pages by one viewport
   *  height each, so a word anywhere in the document can be scrolled to any
   *  vertical position (used to align flagged words to their cards). */
  padEnds?: boolean;
}

export function ScanCanvas({
  paperId,
  pageCount,
  initialPage,
  onActivePageChange,
  zoom,
  onZoomChange,
  onFirstPageRendered,
  scrollRef,
  onPageHeight,
  onPageFootnoteHeight,
  onWordDoubleClick,
  onScroll,
  visibleTypes,
  padEnds,
}: ScanCanvasProps) {
  const [numPages, setNumPages] = useState<number | null>(pageCount);
  const [pageWidths, setPageWidths] = useState<Map<number, number>>(new Map());
  const [pageFootnoteHeight, setPageFootnoteHeight] = useState<Map<number, number>>(new Map());
  const [pdfData, setPdfData] = useState<{ data: ArrayBuffer } | null>(null);
  // One-viewport spacer height for padEnds; tracks the scroll container height.
  const [padPx, setPadPx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const dragStateRef = useRef<{ startX: number; startY: number; scrollLeft: number; scrollTop: number } | null>(null);
  // Refs so the wheel handler (registered once) always sees current values.
  const zoomRef = useRef(zoom);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  const onZoomChangeRef = useRef(onZoomChange);
  useEffect(() => { onZoomChangeRef.current = onZoomChange; }, [onZoomChange]);
  // Reset after each PDF load so onFirstPageRendered fires once per paper.
  const firstPageReportedRef = useRef(false);
  useEffect(() => { if (pdfData === null) firstPageReportedRef.current = false; }, [pdfData]);

  // Forward containerRef to the optional scrollRef from the parent.
  useEffect(() => {
    if (scrollRef && containerRef.current) {
      (scrollRef as React.MutableRefObject<HTMLDivElement | null>).current = containerRef.current;
    }
  });

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/papers/${paperId}/pdf`, { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.arrayBuffer();
      })
      .then((buf) => {
        if (!cancelled) setPdfData({ data: buf });
      })
      .catch((err: unknown) => {
        trackEvent("error", { err_source: "pdf.fetch", err_msg: String(err) });
      });
    return () => {
      cancelled = true;
      setPdfData(null);
    };
  }, [paperId]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      onZoomChangeRef.current(clamp(zoomRef.current + (e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP), MIN_ZOOM, MAX_ZOOM));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Drag-to-pan: click-and-drag scrolls both axes. mousemove/mouseup are on
  // window so the drag continues even when the pointer leaves the container.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    function onMouseDown(e: MouseEvent) {
      if (e.button !== 0) return;
      dragStateRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        scrollLeft: el!.scrollLeft,
        scrollTop: el!.scrollTop,
      };
      el!.style.userSelect = "none";
    }

    function onMouseMove(e: MouseEvent) {
      if (!dragStateRef.current) return;
      const dx = e.clientX - dragStateRef.current.startX;
      const dy = e.clientY - dragStateRef.current.startY;
      if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
      el!.setAttribute("data-dragging", "");
      el!.scrollLeft = dragStateRef.current.scrollLeft - dx;
      el!.scrollTop = dragStateRef.current.scrollTop - dy;
    }

    function onMouseUp() {
      if (!dragStateRef.current) return;
      dragStateRef.current = null;
      el!.removeAttribute("data-dragging");
      el!.style.userSelect = "";
    }

    el.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      el.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  // Track the scroll viewport height so the end spacers are exactly one
  // viewport tall (enough range to align any word to any card line).
  useEffect(() => {
    if (!padEnds) { setPadPx(0); return; }
    const el = containerRef.current;
    if (!el) return;
    const update = () => setPadPx(el.clientHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [padEnds]);

  // Pin the start of the last-active page to the top of the viewport on entry,
  // holding it there as the layout settles (the one-viewport top pad and page
  // sizes arrive over several frames) until the user actually scrolls. Without
  // this, the empty top runway leaves the page scrolled down with blank on top.
  const userScrolledRef = useRef(false);
  const pinnedTopRef = useRef(-1);
  useEffect(() => {
    userScrolledRef.current = false;
    pinnedTopRef.current = -1;
  }, [paperId]);
  useEffect(() => {
    if (userScrolledRef.current) return;
    if (numPages == null || initialPage < 1) return;
    if (padEnds && padPx === 0) return;
    const el = containerRef.current;
    const target = pageRefs.current.get(initialPage);
    if (!el || !target) return;
    target.scrollIntoView({ behavior: "auto", block: "start" });
    pinnedTopRef.current = el.scrollTop;
  }, [numPages, initialPage, padEnds, padPx, pageWidths]);

  useEffect(() => {
    if (numPages == null) return;
    const root = containerRef.current;
    if (!root) return;
    const ratios = new Map<number, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const page = Number(entry.target.getAttribute("data-page"));
          if (!page) continue;
          ratios.set(page, entry.intersectionRatio);
        }
        const dominant = [...ratios.entries()].sort((a, b) => b[1] - a[1])[0];
        if (dominant && dominant[1] > 0.1) onActivePageChange(dominant[0]);
      },
      { root, threshold: [0.1, 0.5, 0.9] },
    );
    for (const el of pageRefs.current.values()) observer.observe(el);
    return () => observer.disconnect();
  }, [numPages, onActivePageChange]);

  // A real user scroll moves scrollTop away from the pinned position; once that
  // happens we stop auto-pinning so the user keeps control. Our own pinning
  // sets pinnedTopRef to match, so it isn't mistaken for a user scroll.
  const handleContainerScroll = () => {
    const el = containerRef.current;
    if (el && pinnedTopRef.current >= 0 && Math.abs(el.scrollTop - pinnedTopRef.current) > 2) {
      userScrolledRef.current = true;
    }
    onScroll?.();
  };

  return (
    <div ref={containerRef} className="scan-canvas" onScroll={handleContainerScroll}>
      <div className="scan-canvas__scroll-inner">
      {padEnds && padPx > 0 && (
        <div className="scan-canvas__end-pad" style={{ height: padPx }} aria-hidden />
      )}
      <Document
        file={pdfData ?? undefined}
        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
        onLoadError={(err) => trackEvent("error", { err_source: "pdf.load", err_msg: err.message })}
        loading={<div className="scan-canvas__loading">Loading…</div>}
        error={<div className="scan-canvas__error">Failed to load PDF.</div>}
      >
        {numPages != null &&
          Array.from({ length: numPages }, (_, i) => i + 1).map((page) => (
            <div
              key={page}
              ref={(el) => {
                if (el) pageRefs.current.set(page, el);
                else pageRefs.current.delete(page);
              }}
              data-page={page}
              className="scan-canvas__page"
              style={{
                paddingRight: Math.round((pageWidths.get(page) ?? 0) * 0.20),
                paddingBottom: pageFootnoteHeight.get(page) ?? 0,
                // Cancels paddingRight from flex centering so the canvas, not the padded box, is centred.
                marginRight: -Math.round((pageWidths.get(page) ?? 0) * 0.20),
              }}
            >
              <Page
                pageNumber={page}
                scale={zoom}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                onRenderSuccess={() => {
                  const canvas = pageRefs.current
                    .get(page)
                    ?.querySelector("canvas") as HTMLCanvasElement | undefined;
                  if (canvas) {
                    setPageWidths((prev) => {
                      if (prev.get(page) === canvas.offsetWidth) return prev;
                      const next = new Map(prev);
                      next.set(page, canvas.offsetWidth);
                      return next;
                    });
                    onPageHeight?.(page, canvas.offsetHeight);
                    if (page === 1 && !firstPageReportedRef.current && onFirstPageRendered) {
                      firstPageReportedRef.current = true;
                      onFirstPageRendered(canvas.offsetWidth / zoom, canvas.offsetHeight / zoom);
                    }
                  }
                }}
              />
              <AnnotationOverlay
                paperId={paperId}
                pageNumber={page}
                widthPx={pageWidthFor(pageRefs.current.get(page))}
                heightPx={pageHeightFor(pageRefs.current.get(page))}
                zoom={zoom}
                onWordDoubleClick={onWordDoubleClick}
                visibleTypes={visibleTypes}
                onFootnoteHeight={(h) => {
                  setPageFootnoteHeight((prev) => {
                    if (prev.get(page) === h) return prev;
                    const next = new Map(prev);
                    next.set(page, h);
                    return next;
                  });
                  onPageFootnoteHeight?.(page, h);
                }}
              />
            </div>
          ))}
      </Document>
      {padEnds && padPx > 0 && (
        <div className="scan-canvas__end-pad" style={{ height: padPx }} aria-hidden />
      )}
      </div>
    </div>
  );
}

function pageWidthFor(el: HTMLDivElement | undefined): number {
  const canvas = el?.querySelector("canvas") as HTMLCanvasElement | undefined;
  return canvas ? canvas.offsetWidth : 0;
}

function pageHeightFor(el: HTMLDivElement | undefined): number {
  const canvas = el?.querySelector("canvas") as HTMLCanvasElement | undefined;
  return canvas ? canvas.offsetHeight : 0;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
